/**
 * WelcomePage.tsx — Erstlauf-Auswahl nach Registrierung (welcome.html)
 *
 * Design-Referenz: heliumtracker_design_prototpye/v2-final/welcome.html.
 * Drei Einstiegs-Auswahlen oeffnen die kanonischen Add-Flows F1 (Hotspot)
 * bzw. F2 (Wallet, eigene/beobachten) — siehe handoff/SPEC.md. Die Modale
 * sind Foundation/Feature-Besitz von hotspots/wallets und werden hier nur
 * eingehaengt, nicht veraendert.
 *
 * Welle 4b / WP-B (Dennis-Entscheid 15:40): loest die WP3-Zusammenlegung
 * (04.09., "eine Kachel, weil Import ohne Signatur unehrlich waere") wieder
 * auf — WP-A macht Owned-Wallet-per-Adresse ohne Signatur moeglich
 * (v1-Paritaet). Wieder ZWEI getrennte Wallet-Kacheln, beide funktionsfaehig:
 * 'Eigene Wallet verbinden' (owned:true, zaehlt ins Plan-Kontingent,
 * Provisionsregeln moeglich) und 'Wallet beobachten' (owned:false, nur
 * Anzeige). Beide oeffnen dasselbe AddWalletModal mit vorgewaehltem Modus
 * (initialMode) — der eigentliche Unterschied wird dort erklaert.
 */
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Wallet, Eye, Radio } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AddWalletModal, type AddWalletMode } from '../wallets/AddWalletModal'
import { AddHotspotModal } from '../hotspots/AddHotspotModal'
import './WelcomePage.css'

type Choice = 'wallet-owned' | 'wallet-watch' | 'hotspot' | null

const WALLET_MODE_BY_CHOICE: Record<'wallet-owned' | 'wallet-watch', AddWalletMode> = {
  'wallet-owned': 'owned',
  'wallet-watch': 'watch',
}

export function WelcomePage() {
  const navigate = useNavigate()
  const { t } = useTranslation('landing')
  const [openChoice, setOpenChoice] = useState<Choice>(null)
  const [done, setDone] = useState(false)

  function closeModal() {
    setOpenChoice(null)
  }

  function select(choice: Exclude<Choice, null>) {
    setDone(true)
    setOpenChoice(choice)
  }

  const isWalletChoice = openChoice === 'wallet-owned' || openChoice === 'wallet-watch'

  function goToDashboard() {
    void navigate({ to: '/dashboard' })
  }

  return (
    <div className="welcome-wrap" data-testid="welcome-page">
      <div className="row" style={{ gap: 10, marginBottom: 26 }}>
        <div className="logo-mark">H</div>
        <strong style={{ fontSize: 16 }}>HeliumTracker</strong>
      </div>

      <div className="welcome-steps" data-testid="welcome-steps">
        <div className="welcome-step-dot on" />
        <div className="welcome-step-dot on" />
        <div className={`welcome-step-dot${done ? ' on' : ''}`} data-testid="welcome-step-3" />
      </div>

      <h1 style={{ margin: '0 0 6px', fontSize: 24 }}>{t('welcome.headline')}</h1>
      <p className="muted" style={{ margin: '0 0 8px', fontSize: 14, lineHeight: 1.6 }}>
        {t('welcome.sub')}
      </p>

      <div className="welcome-choice welcome-choice--triple">
        <button
          type="button"
          data-testid="choice-wallet-owned"
          onClick={() => select('wallet-owned')}
        >
          <span className="t">
            <Wallet size={16} aria-hidden="true" /> {t('welcome.walletOwned.title')}
          </span>
          <span className="d">{t('welcome.walletOwned.description')}</span>
        </button>
        <button
          type="button"
          data-testid="choice-wallet-watch"
          onClick={() => select('wallet-watch')}
        >
          <span className="t">
            <Eye size={16} aria-hidden="true" /> {t('welcome.walletWatch.title')}
          </span>
          <span className="d">{t('welcome.walletWatch.description')}</span>
        </button>
        <button type="button" data-testid="choice-hotspot" onClick={() => select('hotspot')}>
          <span className="t">
            <Radio size={16} aria-hidden="true" /> {t('welcome.hotspot.title')}
          </span>
          <span className="d">{t('welcome.hotspot.description')}</span>
        </button>
      </div>

      <div style={{ textAlign: 'center', marginTop: 10 }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={goToDashboard}
          data-testid="skip-button"
          style={{ fontSize: 13 }}
        >
          {t('welcome.skip')}
        </button>
      </div>

      {done && (
        <div
          className="card card-pad"
          style={{ marginTop: 14, fontSize: 13, color: 'var(--text-muted)' }}
          data-testid="welcome-done-note"
        >
          {t('welcome.doneNote.pre')}{' '}
          <button
            type="button"
            onClick={goToDashboard}
            style={{
              color: 'var(--primary)',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            {t('welcome.doneNote.link')}
          </button>{' '}
          {t('welcome.doneNote.post')}
        </div>
      )}

      <AddWalletModal
        isOpen={isWalletChoice}
        onClose={closeModal}
        onSuccess={closeModal}
        initialMode={
          openChoice === 'wallet-owned' || openChoice === 'wallet-watch'
            ? WALLET_MODE_BY_CHOICE[openChoice]
            : undefined
        }
      />
      <AddHotspotModal isOpen={openChoice === 'hotspot'} onClose={closeModal} />
    </div>
  )
}

export default WelcomePage
